import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import GlassCard from '../ui/GlassCard';
import GlassButton from '../ui/GlassButton';
import SectionHeader from '../ui/SectionHeader';
import { developerResources } from '../../constants/content';
import { track } from '../../lib/analytics';
import { useSectionView } from '../../hooks/useSectionView';

const icons = {
  docs: (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  ),
  github: (
    <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
      <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
    </svg>
  ),
  discord: (
    <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
    </svg>
  )
};

export default function ForDevelopers() {
  const { t } = useTranslation();
  const { darkMode } = useTheme();
  const sectionRef = useSectionView('for_developers', 4);

  return (
    <section
      ref={sectionRef}
      id="developers"
      className={`py-24 ${darkMode ? 'bg-main-black' : 'bg-white'}`}
    >
      <div className="mainCont">
        <SectionHeader
          label={t('developers.label')}
          title={t('developers.headline')}
          subtitle={t('developers.subheadline')}
        />

        {/* Resources Grid */}
        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {developerResources.map((resource) => (
            <GlassCard
              key={resource.id}
              hover
              padding="lg"
              className="flex flex-col h-full"
            >
              {/* Icon */}
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 ${
                darkMode ? 'bg-main/10 text-main' : 'bg-purple-100 text-purple-600'
              }`}>
                {icons[resource.icon]}
              </div>

              {/* Title */}
              <h3 className={`titleFont text-xl font-bold mb-3 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {t(`developers.resources.${resource.id}.title`)}
              </h3>

              {/* Description */}
              <p className={`text-base mb-6 flex-grow ${
                darkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                {t(`developers.resources.${resource.id}.description`)}
              </p>

              {/* CTA */}
              <GlassButton
                variant="ghost"
                size="md"
                href={resource.href}
                onClick={() => {
                  track('cta_clicked', {
                    cta_id: `developers_${resource.id}`,
                    cta_text: t(`developers.resources.${resource.id}.cta`),
                    section: 'for_developers',
                    destination_url: resource.href,
                    destination_type: resource.id === 'docs' ? 'docs' : resource.id === 'github' ? 'github' : 'discord'
                  });
                }}
                className="self-start"
                icon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                }
              >
                {t(`developers.resources.${resource.id}.cta`)}
              </GlassButton>
            </GlassCard>
          ))}
        </div>
      </div>
    </section>
  );
}
